import React, { useState } from 'react';

export default function DateRangePicker({ startDate, endDate, onChange }) {
  const [open, setOpen] = useState(false);
  const [localStart, setLocalStart] = useState(startDate || '');
  const [localEnd, setLocalEnd] = useState(endDate || '');

  const PRESETS = [
    { label: 'Today', getValue: () => {
      const t = new Date();
      const d = t.toISOString().split('T')[0];
      return { start: d, end: d };
    }},
    { label: 'Last 7 Days', getValue: () => {
      const end = new Date();
      const start = new Date(); start.setDate(start.getDate() - 6);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }},
    { label: 'Last 30 Days', getValue: () => {
      const end = new Date();
      const start = new Date(); start.setDate(start.getDate() - 29);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }},
    { label: 'This Month', getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }},
    { label: 'Last Month', getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }},
    { label: 'Year to Date', getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      return { start: start.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
    }},
    { label: 'Last Year', getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end = new Date(now.getFullYear() - 1, 11, 31);
      return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }},
  ];

  const formatDisplay = (d) => {
    if (!d) return '';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const displayText = startDate && endDate
    ? `${formatDisplay(startDate)} – ${formatDisplay(endDate)}`
    : startDate
    ? `From ${formatDisplay(startDate)}`
    : 'Select date range';

  const applyPreset = (preset) => {
    const val = preset.getValue();
    setLocalStart(val.start);
    setLocalEnd(val.end);
    onChange(val.start, val.end);
    setOpen(false);
  };

  const applyCustom = () => {
    if (localStart && localEnd) {
      onChange(localStart, localEnd);
      setOpen(false);
    }
  };

  const clearDates = () => {
    setLocalStart('');
    setLocalEnd('');
    onChange('', '');
    setOpen(false);
  };

  return (
    <div className="relative" style={{ zIndex: 100 }}>
      <button
        data-testid="date-range-picker-trigger"
        onClick={() => setOpen(!open)}
        style={{
          height: '40px',
          padding: '0 12px 0 36px',
          border: '1px solid #DDE7F1',
          borderRadius: '8px',
          background: 'white',
          fontSize: '13px',
          color: '#334155',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          whiteSpace: 'nowrap',
          position: 'relative',
          minWidth: '220px',
        }}
      >
        <i className="fas fa-calendar-alt" style={{ position: 'absolute', left: '12px', color: '#94A3B8' }}></i>
        {displayText}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            background: 'white',
            border: '1px solid #DDE7F1',
            borderRadius: '10px',
            boxShadow: 'var(--shadow-dropdown)',
            padding: '12px',
            zIndex: 999,
            minWidth: '360px',
          }}
        >
          <div className="flex gap-2">
            {/* Presets */}
            <div style={{ width: '140px', borderRight: '1px solid #DDE7F1', paddingRight: '12px' }}>
              <div className="text-xs font-semibold text-slate-500 mb-2">QUICK SELECT</div>
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(p)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '6px 8px', fontSize: '12px', borderRadius: '4px',
                    border: 'none', background: 'none', cursor: 'pointer', color: '#334155',
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#F8FAFC'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Custom range */}
            <div style={{ flex: 1 }}>
              <div className="text-xs font-semibold text-slate-500 mb-2">CUSTOM RANGE</div>
              <div className="mb-3">
                <label className="text-xs text-slate-500 block mb-1">Start Date</label>
                <input
                  type="date"
                  className="input-field"
                  value={localStart}
                  onChange={(e) => setLocalStart(e.target.value)}
                  data-testid="date-range-from-input"
                />
              </div>
              <div className="mb-3">
                <label className="text-xs text-slate-500 block mb-1">End Date</label>
                <input
                  type="date"
                  className="input-field"
                  value={localEnd}
                  onChange={(e) => setLocalEnd(e.target.value)}
                  data-testid="date-range-to-input"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={clearDates} className="btn-secondary" style={{ flex: 1, fontSize: '12px', height: '32px', padding: '0' }}>
                  Clear
                </button>
                <button onClick={applyCustom} className="btn-primary" style={{ flex: 1, fontSize: '12px', height: '32px', padding: '0' }} disabled={!localStart || !localEnd}>
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overlay to close */}
      {open && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50 }}
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
}
